import { Field, InputType, Int, ObjectType } from 'type-graphql'
import { BaseEntity } from '../base/BaseEntity'
import { ChatSender } from './ChatSender'
import { ChatAssignee } from './ChatAssignee'
import { ChatDocument } from './ChatDocument'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { File } from '../file/File'
import { RelatedEntity } from '../base/RelatedEntity'
import { ChatMetadata } from './ChatMetadata'
import type { ChatStatusEnum } from './ChatStatusEnum'
import type { ChatCancelReasonEnum } from './ChatCancelReasonEnum'
import { User } from '../user/User'

// Enum must be declared before the class that uses it
export enum ChatMessageFormatEnum {
  html = 'html',
  text = 'text',
  unknown = 'unknown',
}

@ObjectType()
@InputType('ChatInput')
@modelOptions({ schemaOptions: { timestamps: true, strict: false, collection: 'chats' } })
export class Chat extends BaseEntity {
  @Field(() => ChatSender)
  @Prop({ type: ChatSender })
  sender: ChatSender

  @Field(() => String)
  @Prop({ type: String })
  declare status: ChatStatusEnum

  @Field(() => ChatAssignee, { nullable: true })
  @Prop({ type: ChatAssignee })
  assignee?: ChatAssignee

  @Field(() => User, { nullable: true })
  @Prop({ type: RelatedEntity })
  requester?: ReturnType<() => User>

  @Field(() => Boolean)
  @Prop({ type: Boolean, required: true, default: false })
  isRequesterLinked: boolean

  @Field(() => Date)
  @Prop({ type: Date, required: true })
  lastMessageAt: Date

  @Field(() => Int)
  @Prop({ type: Number, required: true, default: 0 })
  unreadForAgentCount: number

  // Every Message-Id the system has emitted for this conversation — the inbound match set. Never
  // a `@Field`: no client needs it, and keeping it off the schema keeps it off `ChatInput` too.
  @Prop({ type: [String] }, PropType.ARRAY)
  outboundMessageIds: string[]

  // Bare, lower-cased email addresses. The audience of every outbound message alongside
  // the sender's address — the participant set an agent or the requester manages on the conversation.
  @Field(() => [String])
  @Prop({ type: [String], default: [] }, PropType.ARRAY)
  ccEmails: string[]

  @Field(() => String)
  @Prop({ type: String })
  cancelReason?: ChatCancelReasonEnum

  // Pointer back to the cancelled conversation a customer reply continued from: the reply became
  // this conversation's first message instead of reopening the cancelled one. Record-only — unlike
  // `mergedInto` it redirects no later reply.
  @Field(() => Chat, { nullable: true })
  @Prop({ type: RelatedEntity })
  continuedFrom?: ReturnType<() => Chat>

  @Field(() => String)
  @Prop({ type: String })
  subject: string

  @Field(() => String)
  lastMessageContent: string //A field resolver is needed

  @Field(() => Boolean)
  hasUnread: boolean //A field resolver is needed

  @Field(() => [ChatMessage])
  messages: ChatMessage[] //A field resolver is needed

  @Field(() => Int)
  messageCount: number //A field resolver is needed

  @Field(() => String)
  @Prop({ type: String })
  externalId?: string

  @Field(() => Date)
  @Prop({ type: Date })
  timeStamp: Date //this is the external chat timestamp

  @Field(() => ChatMetadata)
  @Prop({ type: ChatMetadata })
  metadata: ChatMetadata

  // The linked business document's `_id` and `code`, copied by the server on create and on a
  // linked-identifier change. Never refreshed after that — a later rename or a hard-deleted
  // sales order does not retroactively change what the conversation shows.
  @Field(() => ChatDocument, { nullable: true })
  @Prop({ type: ChatDocument })
  document?: ChatDocument

  // Tombstone pointer: when set, this conversation was merged into the referenced chat; inbound replies to
  // this chat's old reply token follow the redirect to the target. Internal pointer — not exposed via GraphQL.
  @Prop({ type: String })
  mergedInto?: string

  // Recomputed on every ChatMessage create by ChatSearchableContentRepositoryMiddleware (and explicitly by
  // EmailChatGateway.mergeChat, whose updateMany bypasses postSave hooks). Backfilled for legacy chats by a
  // one-time migration. Internal — not exposed via GraphQL; the inbox "Message" search field regexes this.
  @Prop({ type: String })
  searchableContent?: string

  @Field(() => Boolean)
  isNew: boolean
}

//NOTE: It needs to be in this class to overcome the dependency on ChatMessage.chat field
@ObjectType()
@InputType('ChatMessageInput')
@modelOptions({ schemaOptions: { timestamps: true, strict: false, collection: 'chatmessages' } })
export class ChatMessage extends BaseEntity {
  @Field(() => String)
  @Prop({ type: String })
  externalId: string

  @Field(() => String)
  @Prop({ type: String })
  content: string

  @Field(() => String)
  @Prop({ type: String })
  plainContent: string

  @Field(() => Date)
  @Prop({ type: Date })
  timeStamp: Date //this is the external chat timestamp

  @Field(() => ChatSender)
  @Prop({ type: ChatSender })
  sender: ChatSender

  @Field(() => [File])
  @Prop({ type: [File] })
  attachments: File[]

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isPublic: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean, default: true })
  isUnread: boolean

  @Field(() => Chat)
  @Prop({ type: RelatedEntity })
  chat: Chat

  @Field(() => String)
  @Prop({ type: String })
  format: ChatMessageFormatEnum

  // _id of the authenticated app user who sent an outbound message (the reply invariant records this).
  // Internal audit pointer — not exposed via GraphQL input/output.
  @Prop({ type: String })
  sentByUserId?: string

  // Mailgun inbound authentication verdicts recorded verbatim on a received message. Internal — no GraphQL field.
  @Prop({ type: String })
  spfResult?: string

  @Prop({ type: String })
  dkimResult?: string

  // Set when an inbound reply's From differs from the conversation's known participant — surfaced for agent
  // review rather than silently attributed. Internal — no GraphQL field.
  @Prop({ type: Boolean })
  isFlagged?: boolean
}
